import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { subscribeMeetingIntelEvents } from "../meetingIntelligenceRealtime";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isOperator } from "../../../auth/tier";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import {
  assignmentStatusLabel,
  isAssignmentAccepted,
  isAssignmentInert,
  type ReadAiAssignment,
} from "../meetingIntelligence.types";
import type { ClientModuleRecord } from "../../clients/clients.types";
import type { ModuleCatalogRecord } from "../../modules/modules.types";
import {
  rateModulesForAssignment,
  unassignedGovernedSlug,
  SUGGESTION_MIN_SCORE,
  type ModuleMatchRating,
} from "./moduleSuggestion";
import {
  MEETING_INTEL_SERVER_CAP,
  rowsReturned,
  isAtCap,
  type LoadCap,
} from "shared/truncation";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import type { MeetingIntelEvent } from "realtime";

/**
 * The Meeting Intelligence events that change what Work Status lists — each
 * one a reload. Joined with #3820's finish (2026-11-02): work moved to another
 * module (`assignment-module-changed`: set-module, or a retarget of accepted
 * work, which can also move it to another client), a meeting's items being
 * handed out (`meeting-distributed`, which is when new rows exist, on every
 * path that distributes), and a meeting mapped to a client
 * (`meeting-mapped`: the work it had moves to that client).
 */
export const WORK_STATUS_RELOAD_KINDS: ReadonlySet<MeetingIntelEvent["kind"]> = new Set<MeetingIntelEvent["kind"]>([
  "assignment-accepted",
  "assignment-rejected",
  "assignment-completed",
  "assignment-module-changed",
  "meeting-distributed",
  "meeting-mapped",
]);

/** What `reassign` answers (read-ai-assignments ReassignResult). */
export interface ReassignResponse {
  assignmentId: number;
  status: string;
  userId: number;
  workItemAssignee?: { state: "synced" | "deferred" | "not-filed"; detail: string | null };
}

/** A sentence for a move of accepted work; null when nothing about a card needs saying. */
export const reassignNoticeOf = (result: ReassignResponse | null | undefined): string | null => {
  const card = result?.workItemAssignee;
  if (!card) return null;
  if (card.state === "synced") return "Moved, and its Azure DevOps card with it.";
  if (card.state === "not-filed") return "Moved. Its Azure DevOps card has not been filed yet; it will be filed on the new owner.";
  return `Moved in HQ, but its Azure DevOps card could not be moved yet: ${card.detail ?? "no detail"}`;
};

/**
 * The "Accepted" chip on an accepted card: who accepted it and when. An
 * `accepted` row with no `acceptedBy` was accepted by HQ itself when its
 * meeting's review closed (meeting review §3: no person accepted it). The
 * approval era's `assigned` rows can also lack one, so only `accepted` reads
 * as a review close.
 */
export const acceptedChipLabel = (
  assignment: Pick<ReadAiAssignment, "status" | "acceptedBy">,
  acceptedByName: string,
  acceptedAt: string,
): string => {
  const when = acceptedAt ? ` ${acceptedAt}` : "";
  if (assignment.status === "accepted" && assignment.acceptedBy == null) return `Accepted on review close${when}`;
  if (acceptedByName) return `Accepted by ${acceptedByName}${acceptedAt ? ` · ${acceptedAt}` : ""}`;
  return `Accepted${when}`;
};
/**
 * Status filter for the work-status header. `all` shows everything.
 *
 * These are the LIVE statuses, and each one folds its legacy spelling in (see
 * `matchesStatusFilter`): `available` also matches a not-yet-migrated
 * `pending_review`, and `accepted` also matches the approval era's `assigned`,
 * which is accepted work under the old name.
 */
/** The API's answer to `{ action: 'close-stale' }` — a preview and a run share it. */
export interface CloseStaleResult {
  action: "close-stale";
  preview: boolean;
  cutoff: string;
  olderThanDays: number;
  eligible: number;
  meetings: number;
  accepted: number;
  closed: number;
  assignmentsRejected: number;
  byClient: Array<{ clientId: string | null; clientName: string | null; items: number; meetings: number }>;
}

export type ReviewQueueStatusFilter =
  | "available"
  | "accepted"
  | "completed"
  | "rejected"
  | "all";

/** Scope filter — `mine` restricts to the signed-in user's own assignments. */
export type ReviewQueueScope = "mine" | "all";

/** Does an assignment belong under one of the header's status buckets? */
const matchesStatusFilter = (
  status: string | undefined,
  filter: ReviewQueueStatusFilter
): boolean => {
  switch (filter) {
    case "all":
      return true;
    case "available":
      return isAssignmentInert(status);
    case "accepted":
      return isAssignmentAccepted(status);
    default:
      return status === filter;
  }
};

/** Minimal user shape for the reassign picker. */
export interface ReviewQueueUser {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * One CSV row of the work-status export — WHERE EACH PIECE OF WORK STANDS: who
 * it reached, whether a developer took it, when, and which Azure DevOps work
 * item their acceptance filed. Keys are the `ColumnConfig.key` values in
 * `ReviewQueue.Header.tsx`.
 *
 * The old `autoApproved` column is gone with the gate it recorded: it could
 * only ever say "no" on work created since 2026-09-18, which is a column that
 * answers a question nobody can ask any more. `acceptedBy` / `acceptedAt` /
 * `adoState` replace it, because those are what actually decide whether a
 * ticket exists.
 */
export interface AssignmentExportRow extends Record<string, unknown> {
  id: number;
  status: string;
  title: string;
  priority: string;
  confidence: number | "";
  assignee: string;
  assigneeEmail: string;
  client: string;
  partner: string;
  module: string;
  meetingId: number | "";
  meeting: string;
  dueDate: string;
  acceptedBy: string;
  acceptedAt: string;
  adoState: string;
  decidedBy: string;
  decidedAt: string;
  workItemId: string;
  workItemUrl: string;
  createdAt: string;
}

/** ISO-8601 for a spreadsheet; "" for missing/unparseable. Deliberately NOT the
 *  export toolkit's `format: "date"`, which drops the time component. */
const isoOrEmpty = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toISOString();
};

/**
 * View store for Meeting Intelligence → **Work Status** (route `ReviewQueue`,
 * path `/review-queue` — the route NAME and PATH are wire formats, kept; what a
 * human reads is what changed).
 *
 * ── IT IS NO LONGER AN APPROVAL QUEUE ──────────────────────────────────────
 *
 * Until 2026-09-18 this screen WAS the gate: an operator approved a
 * `pending_review` assignment and THAT created the Azure DevOps work item. That
 * is gone (plan/2026-09-18_suggestions-and-ado-as-a-ticket-database.md, ruling
 * R2). A distributed item goes straight to its assignee's Gantry queue, and the
 * ADO work item is created when THE DEVELOPER ACCEPTS it there. `approve` is
 * not a verb `read-ai-assignments` has any more — it is refused by name — so
 * there is deliberately no `approve()` here to call.
 *
 * What this screen is now: WHERE WORK STANDS — available / accepted /
 * completed / declined, who accepted it and when, and the work item their
 * acceptance filed. It keeps exactly the three operator verbs the API still
 * honours, and only on INERT rows (`isAssignmentInert`): decline, reassign, and
 * set the tracked module. Nothing here accepts work on a developer's behalf:
 * accepting is a statement that somebody is doing it, and HQ is not where that
 * statement is made.
 *
 * The safeguard that replaced the approval step is the separate ROUTING ISSUES
 * list (`views/meetingIntelligence/RoutingIssues`) — work that reached nobody,
 * or that can reach nobody in Azure DevOps.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. Backed by the registered `read-ai-assignments` ServiceStore
 * (see serviceStores/meetingIntelligence.stores.ts) plus the builtin `userStore`
 * for the reassign picker. Per the MobX rule the assignment list and user list
 * are computed views over their ServiceStore entity caches; mutations go through
 * custom POST actions (`reject`/`reassign`) and the `set-module` patch.
 */
class ReviewQueueStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  // Header filters. `all` by default: the screen answers "where does work
  // stand", and opening it already narrowed to one bucket would answer a
  // narrower question than the one it is for.
  statusFilter: ReviewQueueStatusFilter = "all";
  scope: ReviewQueueScope = "all";
  searchQuery = "";
  /**
   * Non-null ⇒ one client's work only, asked of the SERVER (`clientId` on the
   * find), so the 500-row cap applies to that client's rows rather than
   * hiding them behind everyone else's. Set by a `?client=<id>` link — the
   * Whiteboard's "Open in Work Status" on a client lane (#2516).
   */
  clientFilter: string | null = null;

  // Per-row action state.
  processingId: number | null = null;
  /** A refused decline or module change on the list — shown above it. */
  actionError: string | null = null;
  /**
   * What a move of ACCEPTED work did to its Azure DevOps card (meeting review §4):
   * the server moves the card in the same act, or defers it with the reason.
   * Shown on the page, after the dialog closes.
   */
  reassignNotice: string | null = null;
  /**
   * A refused reassign. It runs from the Reassign dialog, whose route throws
   * this, so it shows once, in that dialog's footer — not on the page behind.
   */
  reassignError: string | null = null;
  /**
   * The backlog reset (`read-ai-action-items` `close-stale`). `stalePreview`
   * is the server's count for the window the operator typed; `staleResult`
   * is what the run did. Both are the API's own shape, never derived here.
   */
  stalePreview: CloseStaleResult | null = null;
  staleResult: CloseStaleResult | null = null;
  staleBusy = false;
  staleError: string | null = null;

  // Reassign dialog state.
  reassigningId: number | null = null;
  reassignTargetUserId: number | null = null;

  /**
   * Rows the last successful load returned. Null before the first load, and
   * left alone when a load resolves nothing (a swallowed transport error), so a
   * failed refresh cannot quietly retract a cap the UI already reported.
   */
  loadedRowCount: number | null = null;

  // Whether the reassign-picker user list has been fetched (the list itself is
  // a computed view over the userStore cache).
  private usersLoaded = false;

  // In-flight module fetch, shared so concurrent callers await one request.
  // Deliberately NOT a "loaded once" latch: the header Refresh button re-runs
  // the route's onEnter (routerStore.triggerEnterSideEffects), and a latch here
  // is exactly what kept newly assigned client modules out of the
  // tracked-module picker until a full page reload.
  private modulesLoading: Promise<void> | null = null;

  private unsubscribe: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `read-ai-assignments` ServiceStore. */
  private get assignmentsService(): ServiceStoreType | null {
    return (
      (this.rootStore.readAiAssignmentsStore as ServiceStoreType | null) ?? null
    );
  }

  /** The `read-ai-action-items` ServiceStore — where a close is written. */
  private get actionItemsService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "read-ai-action-items"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The builtin `user` ServiceStore (powers the reassign picker). */
  private get usersService(): ServiceStoreType | null {
    return (this.rootStore.userStore as ServiceStoreType | null) ?? null;
  }

  /** The `client-modules` ServiceStore (powers the tracked-module picker). */
  private get clientModulesService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("client-modules") ??
        this.rootStore.clientModulesStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  /** The `module-catalog` ServiceStore — the governed vocabulary (aliases
   *  included) the tracked-module SUGGESTION resolves through. */
  private get moduleCatalogService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-catalog") ??
        this.rootStore.moduleCatalogStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  // ---- Derived data -------------------------------------------------------

  /**
   * Assignment list. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  /**
   * The assignments the page shows. The ServiceStore MERGES every find into one
   * cache, so after the unfiltered page has loaded, a client-scoped find adds to
   * rows of every other client rather than replacing them: the client filter is
   * applied here too, to the list, the tiles and the export alike (Codex r1).
   */
  get assignments(): ReadAiAssignment[] {
    const all = (this.assignmentsService?.entities ?? []) as unknown as ReadAiAssignment[];
    const client = this.clientFilter;
    return client ? all.filter((a) => a.clientId === client) : all;
  }

  /** User options for the reassign picker (computed over the userStore cache). */
  get users(): ReviewQueueUser[] {
    return (this.usersService?.entities ?? []) as unknown as ReviewQueueUser[];
  }

  /** All loaded client modules (computed view over the `client-modules` cache). */
  get clientModules(): ClientModuleRecord[] {
    return (this.clientModulesService?.entities ??
      []) as unknown as ClientModuleRecord[];
  }

  /**
   * Module options for one assignment's tracked-module picker — the modules of
   * THE client the assignment resolves to (the API rejects cross-client picks).
   * Empty when the assignment has no resolvable client.
   */
  modulesForClient(clientId?: string | null): ClientModuleRecord[] {
    if (!clientId) return [];
    return this.clientModules
      .filter((m) => m.clientId === clientId && !m.deletedAt)
      .slice()
      .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
  }

  /** Governed vocabulary (computed view over the `module-catalog` cache). */
  get moduleCatalog(): ModuleCatalogRecord[] {
    return (this.moduleCatalogService?.entities ??
      []) as unknown as ModuleCatalogRecord[];
  }

  // ---- Tracked-module suggestion (see moduleSuggestion.ts) ----------------

  /**
   * Heuristic ratings of the client's modules against one assignment, best
   * first. Pure view over the assignment + module + catalog caches.
   */
  ratingsFor(assignment: ReadAiAssignment): ModuleMatchRating[] {
    const modules = this.modulesForClient(assignment.clientId);
    if (modules.length === 0) return [];
    return rateModulesForAssignment(assignment, modules, this.moduleCatalog);
  }

  /**
   * THE suggestion for an assignment's tracked module: the top-rated candidate
   * at or above the display threshold — only while the item is still INERT and
   * has no module set (a made choice is never second-guessed, and the API
   * refuses `set-module` on work a developer has already accepted).
   */
  suggestionFor(assignment: ReadAiAssignment): ModuleMatchRating | null {
    if (assignment.moduleId || !isAssignmentInert(assignment.status)) {
      return null;
    }
    const top = this.ratingsFor(assignment)[0];
    return top && top.score >= SUGGESTION_MIN_SCORE ? top : null;
  }

  /**
   * Picker options for one assignment: rated candidates first (best rating
   * down), then the rest alphabetically — so the likely module is at the top
   * of the dropdown, and every module stays reachable.
   */
  rankedModulesForClient(assignment: ReadAiAssignment): ClientModuleRecord[] {
    const all = this.modulesForClient(assignment.clientId);
    const ratings = this.ratingsFor(assignment);
    const rated = ratings.map((r) => r.module);
    const ratedIds = new Set(rated.map((m) => m.id));
    return [...rated, ...all.filter((m) => !ratedIds.has(m.id))];
  }

  /** Rating per module id for option labels ("· 75% match"). */
  ratingsByModuleId(assignment: ReadAiAssignment): Map<string, number> {
    return new Map(this.ratingsFor(assignment).map((r) => [r.module.id, r.score]));
  }

  /**
   * Non-null when extraction attributed the item to a governed module the
   * client has NO client_modules row for — the module genuinely cannot appear
   * in the picker until it is assigned to the client in Client 360, and the
   * queue says so instead of looking broken.
   */
  unassignedGovernedHint(assignment: ReadAiAssignment): string | null {
    if (assignment.moduleId || !isAssignmentInert(assignment.status)) {
      return null;
    }
    return unassignedGovernedSlug(
      assignment,
      this.modulesForClient(assignment.clientId),
      this.moduleCatalog
    );
  }

  // ---- Filter setters -----------------------------------------------------

  setStatusFilter(filter: ReviewQueueStatusFilter) {
    // Pure client-side filter (see filteredAssignments) — the full set is already
    // loaded, so no refetch is needed and the summary counts stay accurate.
    this.statusFilter = filter;
  }

  setScope(scope: ReviewQueueScope) {
    this.scope = scope;
    void this.load();
  }

  /** A `?client=<uuid>` link narrows the list to that client; any other value, or none, shows everyone's. */
  showClientFromLink(raw: unknown) {
    this.clientFilter =
      typeof raw === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw) ? raw : null;
  }

  /**
   * The client the list is narrowed to: by name when a loaded row or the
   * clients list names it, else "the client from the link" — never an id.
   */
  get clientFilterName(): string | null {
    if (!this.clientFilter) return null;
    const fromRows = this.assignments.find((a) => a.clientId === this.clientFilter)?.clientName;
    if (fromRows) return fromRows;
    const clients = ((this.rootStore.serviceStoreManager?.getStore?.("clients") ?? this.rootStore.clientStore) as unknown as
      | { entities?: Array<{ id?: unknown; name?: unknown }> }
      | null
      | undefined)?.entities;
    const fromClients = clients?.find((c) => c.id === this.clientFilter)?.name;
    return typeof fromClients === "string" && fromClients ? fromClients : "the client from the link";
  }

  /**
   * Drop the client filter and re-read everyone's work. The URL goes back to
   * the plain page too, so a refresh does not bring the filter back — but
   * navigating to the route already shown does not re-run its onEnter, so the
   * re-read is this method's own (a browser check caught the list staying
   * narrowed after the chip had gone).
   */
  clearClientFilter() {
    this.clientFilter = null;
    void this.load();
    // An EMPTY query bag (the URL otherwise keeps `?client=`), written in place:
    // clearing a filter is not a page Back should return to.
    const router = this.rootStore.routerStore as
      | { goTo?: (name: string, params: null, query: Record<string, never>, options: { replace: boolean }) => unknown }
      | undefined;
    void router?.goTo?.("ReviewQueue", null, {}, { replace: true });
  }

  setSearchQuery(q: string) {
    // Pure client-side filter, composed with statusFilter in filteredAssignments.
    this.searchQuery = q;
  }

  /** True when a search, status or client filter narrows the list. */
  get hasActiveFilter(): boolean {
    return this.searchQuery.trim().length > 0 || this.statusFilter !== "all" || this.clientFilter !== null;
  }

  get filteredAssignments(): ReadAiAssignment[] {
    let result = this.assignments;
    if (this.statusFilter !== "all") {
      result = result.filter((a) => matchesStatusFilter(a.status, this.statusFilter));
    }
    const q = this.searchQuery.toLowerCase().trim();
    if (q) {
      result = result.filter(
        (a) =>
          a.title?.toLowerCase().includes(q) ||
          a.meetingTitle?.toLowerCase().includes(q) ||
          a.assigneeName?.toLowerCase().includes(q)
      );
    }
    return result;
  }

  /** In an assignee's queue, nobody has taken it, nothing exists in ADO yet. */
  get availableCount(): number {
    return this.assignments.filter((a) => isAssignmentInert(a.status)).length;
  }

  /** A developer said they are doing it — the moment the ADO work item is filed. */
  get acceptedCount(): number {
    return this.assignments.filter((a) => isAssignmentAccepted(a.status)).length;
  }

  get completedCount(): number {
    return this.assignments.filter((a) => a.status === "completed").length;
  }

  get rejectedCount(): number {
    return this.assignments.filter((a) => a.status === "rejected").length;
  }

  /** The status as a human reads it, legacy spellings folded into meaning. */
  statusLabel(assignment: ReadAiAssignment): string {
    return assignmentStatusLabel(assignment.status);
  }

  /** Whether HQ's three operator verbs may be offered on this row. */
  canActOn(assignment: ReadAiAssignment): boolean {
    return isAssignmentInert(assignment.status);
  }

  /**
   * Meeting review §4: ACCEPTED work (a card exists, or is being filed) may be
   * MOVED to someone else by an operator — the one verb it takes; the server
   * moves the card with it. A hint: the API re-checks the tier.
   */
  canMoveAccepted(assignment: ReadAiAssignment): boolean {
    return isAssignmentAccepted(assignment.status) && isOperator(this.rootStore);
  }

  clearReassignNotice() {
    this.reassignNotice = null;
  }

  /** Label for a user option in the reassign picker. */
  userLabel(user: ReviewQueueUser): string {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    if (name && user.email) return `${name} <${user.email}>`;
    if (name) return name;
    return user.email || `User #${user.id}`;
  }

  /**
   * A user id as a name. `read_ai_assignments` stores only ids (`acceptedBy`,
   * `reviewedBy`), so this resolves through whatever the reassign picker has
   * already loaded and degrades to `User #<id>` rather than inventing a name.
   * "" when there is no id — i.e. nobody has done the thing yet.
   */
  reviewerLabel(userId?: number | null): string {
    if (userId == null) return "";
    const user = this.users.find((u) => u.id === userId);
    if (!user) return `User #${userId}`;
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    return name || user.email || `User #${userId}`;
  }

  // ---- Load cap (see ../loadCap.ts) ---------------------------------------

  /** What the last load asked for vs. what came back. */
  get loadCap(): LoadCap {
    return {
      requested: MEETING_INTEL_SERVER_CAP,
      returned: this.loadedRowCount,
    };
  }

  /**
   * True when the loaded set is at the server's cap — i.e. the pending/assigned/
   * rejected counts are LOWER BOUNDS, not totals. `read-ai-assignments` returns
   * a bare array with no `total` and honours no `$skip`; see ../loadCap.ts.
   */
  get isTruncated(): boolean {
    return isAtCap(this.loadCap);
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * The CSV shape of WHAT IS ON SCREEN — `filteredAssignments`, so the file
   * carries the active status filter, scope and search. Exporting a fresh
   * unfiltered fetch would silently disagree with the queue on screen.
   * Pure: no I/O, no side effects, safe to snapshot in a test.
   */
  get exportRows(): AssignmentExportRow[] {
    return this.filteredAssignments.map((a) => ({
      id: a.id,
      status: a.status ?? "",
      title: a.title ?? "",
      priority: a.priority ?? "",
      confidence: a.confidence ?? "",
      assignee: a.assigneeName ?? "",
      assigneeEmail: a.assigneeEmail ?? "",
      client: a.clientName ?? "",
      partner: a.partnerName ?? "",
      module: a.moduleName ?? "",
      meetingId: a.meetingId ?? "",
      meeting: a.meetingTitle ?? "",
      dueDate: isoOrEmpty(a.dueDate),
      acceptedBy: this.reviewerLabel(a.acceptedBy),
      acceptedAt: isoOrEmpty(a.acceptedAt),
      // "" means NOTHING HAS BEEN ATTEMPTED, which on an available row is the
      // correct state — ADO hears about work when somebody accepts it.
      adoState: a.adoState ?? "",
      decidedBy: this.reviewerLabel(a.reviewedBy),
      decidedAt: isoOrEmpty(a.reviewedAt),
      workItemId: a.externalWorkItemId ?? "",
      workItemUrl: a.externalWorkItemUrl ?? "",
      createdAt: isoOrEmpty(a.createdAt),
    }));
  }

  /** `exportToCsv` throws on an empty set, so the button is disabled instead. */
  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      // Load the full set (scope only) so the summary counts reflect every
      // status; the table is narrowed client-side by statusFilter. The find
      // populates the entity cache read by the `assignments` getter.
      //
      // MEETING_INTEL_SERVER_CAP is the service's own hard clamp, not a
      // preference — `read-ai-assignments` does `Math.min($limit, 500)`,
      // returns a bare array with no total, and ignores $skip, so this is one
      // page and there is no second one to ask for. Record how many rows
      // actually came back: returning exactly the cap is what tells the UI the
      // counts are lower bounds (see ../loadCap.ts).
      const query: Record<string, unknown> = {
        $limit: MEETING_INTEL_SERVER_CAP,
        $sort: { createdAt: -1 },
      };
      if (this.scope === "mine") {
        query.mine = "true";
      }
      if (this.clientFilter) {
        query.clientId = this.clientFilter;
      }
      // findOrThrow: a swallowed failure rendered "No assignments in this
      // view." with every tile at 0.
      const response = await findOrThrow(this.assignmentsService, { query });
      const returned = rowsReturned(response);
      if (returned !== null && !ended()) {
        runInAction(() => {
          this.loadedRowCount = returned;
        });
      }
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) this.error = loadErrorMessage(err, "Failed to load assignments");
      });

      console.error("Failed to load review queue:", err);
    } finally {
      // Signed out meanwhile: reset() cleared the flag, and a load the next
      // person started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Refresh after a write or a realtime event — assignments only (the module
   *  vocabulary doesn't change because a developer accepted something). */
  async reload() {
    await this.load();
  }

  /** Load the user list that powers the reassign picker. */
  async loadUsers() {
    if (this.usersLoaded) return;
    const ended = sessionMark(this.rootStore);
    try {
      // findOrThrow: the library store swallows a failed find (the session
      // guard's rejection included), and a swallowed one latched the picker
      // empty for the store's lifetime.
      await findOrThrow(this.usersService, { query: { $limit: 500 } });
      if (ended()) return;
      runInAction(() => {
        this.usersLoaded = true;
      });
    } catch (err: unknown) {
      if (ended()) return;
      console.error("Failed to load users for reassign picker:", err);
    }
  }

  /**
   * (Re)load the client-modules list that powers the tracked-module picker,
   * plus the governed catalog the suggestion resolves through. Called from the
   * route's onEnter — which the header Refresh button re-runs — so EVERY visit
   * and every explicit refresh picks up modules assigned since the last fetch.
   * Concurrent callers share one in-flight request.
   */
  async loadModules() {
    if (this.modulesLoading) return this.modulesLoading;
    const run = (async () => {
      try {
        await Promise.all([
          this.clientModulesService?.find({ query: { $limit: 500 } }),
          this.moduleCatalogService?.find({ query: { $limit: 500 } }),
        ]);
      } catch (err: unknown) {
        // Non-fatal: the picker renders whatever the cache already holds.
        console.error("Failed to load modules for tracked-module picker:", err);
      }
    })();
    this.modulesLoading = run;
    try {
      await run;
    } finally {
      runInAction(() => {
        // Only our own handle: a sign-out's reset() may have cleared it and the
        // next person's load taken the slot.
        if (this.modulesLoading === run) this.modulesLoading = null;
      });
    }
  }

  // ---- Reassign dialog state ----------------------------------------------

  startReassign(id: number) {
    this.reassigningId = id;
    this.reassignTargetUserId = null;
    this.reassignError = null;
  }

  setReassignTargetUserId(userId: number | null) {
    this.reassignTargetUserId = userId;
  }

  /** Submit handler wired to the reassign dialog's `onSubmit`. */
  async submitReassign(): Promise<boolean> {
    if (this.reassigningId == null || this.reassignTargetUserId == null) {
      return false;
    }
    return this.reassign(this.reassigningId, this.reassignTargetUserId);
  }

  // ---- Custom actions -----------------------------------------------------

  // THERE IS NO `approve()`, AND THERE MUST NOT BE ONE.
  //
  // `read-ai-assignments` refuses `action: 'approve'` by name — the dispatcher
  // answers "Approval was removed on 2026-09-18… the Azure DevOps work item is
  // created when the developer accepts it". A method here would render a button
  // whose only possible outcome is that message.
  //
  // There is no `accept()` either, and that is a choice rather than an
  // omission: the API would take one from an operator, but accepting means "I
  // am doing this work", and HQ is not where a developer says that — their
  // Gantry is. An operator who wants the work to go away declines it.

  /** Decline an item on the assignee's behalf. Inert rows only (the API
   *  refuses anything a developer has already accepted). */
  // ---- The backlog reset ---------------------------------------------------

  /** Count what a `close-stale` of this window would do. Writes nothing. */
  async previewStale(olderThanDays: number): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.staleBusy = true;
    this.staleError = null;
    this.staleResult = null;
    try {
      // THROW_ON_ERROR: the ServiceStore otherwise RESOLVES a refusal as
      // null, which read as "nothing to count" with no reason shown.
      const r = (await this.actionItemsService?.create(
        { action: "close-stale", olderThanDays, preview: true } as never,
        THROW_ON_ERROR,
      )) as unknown as CloseStaleResult | null | undefined;
      // A ServiceStore resolves null when a sign-out ended the call: that is
      // not "Could not count stale items" on the next person's page.
      if (ended()) return;
      if (!r) throw new Error("Could not count stale items");
      runInAction(() => {
        this.stalePreview = r;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) this.staleError = err instanceof Error ? err.message : "Could not count stale items";
      });
    } finally {
      // Signed out meanwhile: reset() cleared the flag, and a call the next
      // person started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.staleBusy = false;
        });
      }
    }
  }

  /** Close every open item on a meeting older than the window. Reloads the queue after. */
  async closeStale(olderThanDays: number, includeAccepted: boolean): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.staleBusy = true;
    this.staleError = null;
    try {
      // THROW_ON_ERROR: a refusal used to RESOLVE null, which this read as a
      // finished reset — the preview vanished, the queue reloaded, no reason.
      const r = (await this.actionItemsService?.create(
        { action: "close-stale", olderThanDays, includeAccepted } as never,
        THROW_ON_ERROR,
      )) as unknown as CloseStaleResult | null | undefined;
      if (ended()) return false;
      if (!r) throw new Error("The backlog reset failed");
      runInAction(() => {
        this.staleResult = r;
        this.stalePreview = null;
      });
      await this.reload();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) this.staleError = err instanceof Error ? err.message : "The backlog reset failed";
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.staleBusy = false;
        });
      }
    }
  }

  clearStale(): void {
    this.stalePreview = null;
    this.staleResult = null;
    this.staleError = null;
  }

  async reject(id: number, reason?: string): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.processingId = id;
    this.actionError = null;
    try {
      await this.assignmentsService?.create(
        { action: "reject", id, reason } as never,
        THROW_ON_ERROR,
      );
      if (ended()) return false;
      await this.reload();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) {
          this.actionError =
            err instanceof Error ? err.message : "Failed to decline assignment";
        }
      });

      console.error("decline assignment failed:", err);
      return false;
    } finally {
      // Signed out meanwhile: reset() cleared the flag, and an action the next
      // person started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.processingId = null;
        });
      }
    }
  }

  async reassign(id: number, userId: number): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.processingId = id;
    this.reassignError = null;
    this.reassignNotice = null;
    try {
      const result = (await this.assignmentsService?.create(
        { action: "reassign", id, userId } as never,
        THROW_ON_ERROR,
      )) as unknown as ReassignResponse | null | undefined;
      if (ended()) return false;
      runInAction(() => {
        this.reassignNotice = reassignNoticeOf(result);
      });
      await this.reload();
      // reload() is load(): it swallows a sign-out and resolves, so this tail
      // would close the NEXT person's reassign dialog.
      if (ended()) return false;
      runInAction(() => {
        this.reassigningId = null;
        this.reassignTargetUserId = null;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) {
          this.reassignError =
            err instanceof Error ? err.message : "Failed to reassign assignment";
        }
      });

      console.error("reassign assignment failed:", err);
      return false;
    } finally {
      // Signed out meanwhile: reset() cleared the flag, and an action the next
      // person started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.processingId = null;
        });
      }
    }
  }

  /**
   * Set (or clear, with `null`) the client module an INERT assignment is
   * tracked against — and, because the module is what decides which Azure
   * DevOps project the work item lands in, what makes it routable at all when
   * a developer accepts it. Goes through the `set-module` action on the
   * `read-ai-assignments` service, then refreshes the list.
   */
  async setAssignmentModule(
    assignmentId: number,
    moduleId: string | null
  ): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.processingId = assignmentId;
    this.actionError = null;
    try {
      await this.assignmentsService?.patch(
        assignmentId,
        { action: "set-module", id: assignmentId, moduleId } as never,
        THROW_ON_ERROR,
      );
      if (ended()) return false;
      await this.reload();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) {
          this.actionError =
            err instanceof Error ? err.message : "Failed to set tracked module";
        }
      });

      console.error("set-module assignment failed:", err);
      return false;
    } finally {
      // Signed out meanwhile: reset() cleared the flag, and an action the next
      // person started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.processingId = null;
        });
      }
    }
  }

  // ---- Realtime -----------------------------------------------------------

  /** Subscribe to the events that change this list, so it stays fresh: work
   *  accepted, declined or completed; moved to another module (set-module, a
   *  retarget); a meeting's items handed out (`meeting-distributed`: new rows
   *  exist); a meeting mapped to a client. `assignment-approved` is gone from the API's `events` with
   *  approval itself — a handler still listening for it would never fire
   *  again. The API narrows every one of these to the people who may see a
   *  private meeting, so nothing arrives that this list could not show. */
  subscribe() {
    if (this.unsubscribe) return;
    // Shared realtime bus (src/realtime) — auth is sourced internally from the
    // AuthStore. This subscription used to be silently inert because it was
    // made with no token; that state is no longer expressible.
    this.unsubscribe = subscribeMeetingIntelEvents(this.rootStore, (event) => {
      if (WORK_STATUS_RELOAD_KINDS.has(event.kind)) void this.reload();
    });
  }

  dispose() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.reassignError = null;
    this.reassignNotice = null;
    this.dispose();
    this.loading = false;
    this.error = null;
    this.statusFilter = "all";
    this.scope = "all";
    this.searchQuery = "";
    this.clientFilter = null;
    this.processingId = null;
    this.actionError = null;
    this.stalePreview = null;
    this.staleResult = null;
    this.staleBusy = false;
    this.staleError = null;
    this.reassigningId = null;
    this.reassignTargetUserId = null;
    this.loadedRowCount = null;
    this.usersLoaded = false;
    this.modulesLoading = null;
    this.unsubscribe = null;
  }
}

export default ReviewQueueStore;
